import { Box, Grid, Skeleton, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import dayjs from 'dayjs';
import TimeRangeSelector from './TimeRangeSelector';
import { useGetFinancialOverviewDataHandler } from '../dataHandlers/getFinancialOverviewDataHandler';
import { currencyFormatter } from '../../../utils/currencyFormat';
import SalesPurchaseTrendChart from './SalesPurchaseTrendChart';
import { DEFAULT_CURRENCY_CODE } from '../../../constants/defaults';
import {
  formatDateRangeLabel,
  formatDateToDashboardFormat,
  formatToIsoDate,
} from '../../../utils/formatDate';
import {formatAccessDeniedMessage} from "../../../utils/accessDeniedMessage";
import {useSelector} from "react-redux";
import AccessDeniedCard from "../../../components/AccessDeniedCard";
import { SALES_MODULE_KEY, PURCHASE_MODULE_KEY } from '../../settings/constants/roleModules';
import {useRoleModuleLabel} from "../../../hooks/useRoleModuleLabel";
import AccessDeniedComplienceSection from "./AccessDeniedComplienceSection";

const computeDateParams = (range) => {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();

  const makeDate = (y, m, d) => new Date(Date.UTC(y, m, d));
  const format = (date) => formatToIsoDate(date);

  const ranges = {
    current_month: {
      start: makeDate(year, month, 1),
      end: makeDate(year, month + 1, 0),
      groupedBy: 'DAYS',
      interval: 7,
    },
    previous_month: {
      start: makeDate(year, month - 1, 1),
      end: makeDate(year, month, 0),
      groupedBy: 'DAYS',
      interval: 7,
    },
    previous_quarter: {
      start: makeDate(year, month - 3, 1),
      end: makeDate(year, month, 0),
      groupedBy: 'MONTHS',
    },
    previous_2_quarters: {
      start: makeDate(year, month - 6, 1),
      end: makeDate(year, month, 0),
      groupedBy: 'MONTHS',
    },
  };

  const selected = ranges[range] || ranges.previous_2_quarters;

  return {
    startDate: format(selected.start),
    endDate: format(selected.end),
    groupedBy: selected.groupedBy,
    interval: selected.interval,
  };
};

const coerceNumber = (value) => {
  if (value === null || value === undefined) {
    return 0;
  }

  const numeric = Number(value);
  return Number.isNaN(numeric) ? 0 : numeric;
};

const getLocalizedMonthLabel = (sale, language='en') => {
  const parsedFromStart = dayjs(sale?.start);
  if (parsedFromStart.isValid()) {
    return parsedFromStart.locale(language || 'en').format('MMMM');
  }

  const parsedFromMonth = dayjs(sale?.month, 'MMMM', 'en', true);
  if (parsedFromMonth.isValid()) {
    return parsedFromMonth.locale(language || 'en').format('MMMM');
  }

  return sale?.month || '';
};

const buildTrendData = (financialOverview, groupedBy) => {
  const trend = financialOverview?.payable_amount_trend_data;
  const merged = trend?.sales.map((sale) => {
    const match = trend?.purchases.find((p) => p.start === sale.start && p.end === sale.end) || {};
    const label =
      groupedBy === 'MONTHS'
        ? getLocalizedMonthLabel(sale)
        : formatDateRangeLabel({ start: sale.start, end: sale.end });

    return { label, sales: sale.amount, purchases: match.amount || 0 };
  });

  return merged;
};

const formatRangeTooltip = (range, separator = 'to') => {
  const { startDate, endDate } = computeDateParams(range);
  return `${formatDateToDashboardFormat(startDate)} ${separator} ${formatDateToDashboardFormat(endDate)}`;
};

const FinancialOverviewSection = ({canReadSalesAndPurchaseInvoice}) => {
  const [timeRange, setTimeRange] = useState('previous_quarter');

  const params = computeDateParams(timeRange);

  const canFetch =
    Boolean(params.startDate && params.endDate && params.groupedBy);

  const { financialOverview, isLoading } = useGetFinancialOverviewDataHandler({
    ...params,
    enabled: canFetch,
  });

  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };

  const vatSummary = financialOverview?.vat_summary ?? {};
  const salesVatAmount = coerceNumber(vatSummary.sales) || 0;
  const purchasesVatAmount = coerceNumber(vatSummary.purchases) || 0;
  const netVat = salesVatAmount - purchasesVatAmount;

  const formatter = currencyFormatter(DEFAULT_CURRENCY_CODE);

  const summaryCards = [
    {
      key: 'salesVat',
      title: "Total Output VAT",
      value: formatter.format(salesVatAmount),
      subtitle: "VAT collected from sales",
    },
    {
      key: 'purchasesVat',
      title: "Total Input VAT",
      value: formatter.format(purchasesVatAmount),
      subtitle: "VAT paid on purchases",
    },
    {
      key: 'netVat',
      title: "Net VAT Payable",
      value: formatter.format(netVat),
      subtitle:
        netVat >= 0
          ? "Amount due to tax authority"
          : "Amount reclaimable",
    },
  ];

  const trendData = buildTrendData(financialOverview, params.groupedBy);

  const showSummarySkeleton = isLoading && !financialOverview;
  const showTrendSkeleton = isLoading && !financialOverview;

  const toSeparator = "to";
  const startDateLabel = `\u200E${formatDateToDashboardFormat(params.startDate).trim()}\u200E`;
  const endDateLabel = `\u200E${formatDateToDashboardFormat(params.endDate).trim()}\u200E`;


  const rootUserEmail = useSelector((state) => state.org.org?.root_user_email ?? '');
  const title = useRoleModuleLabel(SALES_MODULE_KEY)+" & "+ useRoleModuleLabel(PURCHASE_MODULE_KEY);
  const timeRanges = [
    {
      label: "Current Month",
      value: 'current_month',
      tooltip: formatRangeTooltip('current_month', toSeparator),
    },
    {
      label: "Previous Month",
      value: 'previous_month',
      tooltip: formatRangeTooltip('previous_month', toSeparator),
    },
    {
      label: "Previous Quarter",
      value: 'previous_quarter',
      tooltip: formatRangeTooltip('previous_quarter', toSeparator),
    },
    {
      label: "Previous 2 Quarters",
      value: 'previous_2_quarters',
      tooltip: formatRangeTooltip('previous_2_quarters', toSeparator),
    },
  ];
  const getFinancialOverviewSection = () => (
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            {"Financial Overview"}
          </Typography>
          <TimeRangeSelector
              timeRange={timeRange}
              handleTimeRangeChange={handleTimeRangeChange}
              loading={isLoading}
              timeRanges={timeRanges}
          />
        </Stack>
        <Typography variant="body1" sx={{ mb: 1, textAlign: 'right' }}>
          {"Period"}{' '}
          <Box component="span" fontWeight="bold">
            <Box component="span" sx={{ direction: 'ltr', unicodeBidi: 'isolate' }}>
              {startDateLabel}
            </Box>{' '}
            {toSeparator}{' '}
            <Box component="span" sx={{ direction: 'ltr', unicodeBidi: 'isolate' }}>
              {endDateLabel}
            </Box>
          </Box>
        </Typography>

        <Grid container spacing={3} mb={3}>
          {summaryCards.map((card) => (
              <Grid size={{ xs: 12, md: 4 }} key={card.key}>
                <Stack
                    direction="column"
                    justifyContent="space-between"
                    border={2}
                    borderColor="divider"
                    borderRadius={1}
                    p={2}
                    height="100%"
                >
                  <Typography variant="subtitle2" color="text.secondary">
                    {card.title}
                  </Typography>
                  {showSummarySkeleton ? (
                      <Skeleton variant="text" height={42} width="60%" />
                  ) : (
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {card.value}
                      </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {card.subtitle}
                  </Typography>
                </Stack>
              </Grid>
          ))}
        </Grid>

        <SalesPurchaseTrendChart
            data={trendData}
            loading={showTrendSkeleton}
            title={"Sales & Purchase Trends"}
            emptyMessage={"No trend data available for the selected period."}
            totalSalesLabel={"Total Sales"}
            totalPurchasesLabel={"Total Purchases"}
            intervalType={params.groupedBy}
            currencyCode={DEFAULT_CURRENCY_CODE}
        />

        {!financialOverview && !isLoading ? (
            <Typography color="error" variant="h6" sx={{ mt: 3 }}>
              {"No financial overview data available"}
            </Typography>
        ) : null}
      </Box>
  )
  const getAccessDeniedCard = () => {
    const ACCESS_DENIED_MSG = "Access denied, please contact your organization admin ({email}) to get access."
    return (
        <Stack spacing={1} sx={{
          border: '1px solid gray',
          borderRadius: '0.5rem',
          padding: '1rem',

        }}>
          <Typography variant="h7" color="text.secondary">Financial Overview</Typography>
          <Typography variant="body2" color="text.secondary">{formatAccessDeniedMessage(ACCESS_DENIED_MSG,rootUserEmail)}</Typography>
        </Stack>
    )
  }

  return canReadSalesAndPurchaseInvoice ? getFinancialOverviewSection() : <AccessDeniedComplienceSection/>;
};

export default FinancialOverviewSection;
